// react-taskplex-web-journal/src/components/AutoWakeWordLoader.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import * as tf from "@tensorflow/tfjs";
// Import the WASM backend package for its side effects (registration)
import "@tensorflow/tfjs-backend-wasm";
import PropTypes from "prop-types";
import Meyda from "meyda";

// Configuration
const sampleRate = 16000;
const modelPath = "/models/model.json"; // Make sure this path is correct relative to your public folder
// const detectionThreshold = 0.86;
const detectionThreshold = 0.84;
const FFT_SIZE = 512;
const HOP_LENGTH = 256;
const N_MFCC = 40;
const FEATURE_MEAN = -3.3359674282429186;
const FEATURE_STD = 46.60141388518999;
// const WAKE_WORD_BUFFER_DURATION_S = 1.0; // Less relevant now, Meyda controls windowing
const TARGET_FRAMES_FOR_MODEL = 63; // The number of MFCC frames the model expects (input shape: [1, 120, 63, 1])
const DETECTION_COOLDOWN_MS = 1500; // Prevent rapid-fire detections

const AutoWakeWordLoader = ({
  onModelStatusChange,
  onWakeWordDetected,
  onStreamReady,
  enabled, // Controls if processing should happen
}) => {
  const modelRef = useRef(null);
  const [isModelReady, setIsModelReady] = useState(false);
  const audioContextRef = useRef(null);
  const microphoneSourceRef = useRef(null);
  const analyserRef = useRef(null); // Meyda uses this analyser
  const workletNodeRef = useRef(null); // Optional worklet for other uses
  const streamRef = useRef(null);
  const meydaAnalyzerRef = useRef(null);
  const lastDetectionTimeRef = useRef(0);
  const isMountedRef = useRef(true);
  const mfccFramesRef = useRef([]); // Stores the collected MFCC frames [ [mfcc1], [mfcc2], ... ]
  const detectionInProgressRef = useRef(false); // Flag to prevent overlapping detections

  // State for overall processing activity (setup/paused)
  const [isProcessingSetup, setIsProcessingSetup] = useState(false); // Tracks if audio nodes are connected etc.
  const isProcessingSetupRef = useRef(false); // Ref for synchronous checks

  // Sync ref with state
  useEffect(() => {
    isProcessingSetupRef.current = isProcessingSetup;
  }, [isProcessingSetup]);

  // --- Model Loading Effect ---
  useEffect(() => {
    isMountedRef.current = true;
    let localIsMounted = true;

    const loadModel = async () => {
      // console.log(">>> AutoWakeWordLoader: Model loading effect started.");
      try {
        setIsModelReady(false); // Set loading status
        if (onModelStatusChange) onModelStatusChange(false);
        // console.log("Loading wake word model from:", modelPath);

        // Ensure TF core is ready
        await tf.ready();
        // Set the backend - it should be registered because of the import above
        // Wrap in try/catch in case WASM isn't supported or files are missing
        try {
          await tf.setBackend("wasm");
          // console.log("Using TF backend:", tf.getBackend());
        } catch (backendError) {
          console.warn(
            "Failed to set WASM backend, falling back to default:",
            backendError
          );
          // Optionally try WebGL or let TFJS choose the default (often WebGL or CPU)
          // await tf.setBackend('webgl');
          // console.log("Using TF backend:", tf.getBackend());
        }

        const loadedModel = await tf.loadLayersModel(modelPath);

        if (localIsMounted) {
          // console.log(
          //   "Model loaded. Input shape:",
          //   loadedModel.inputs[0].shape
          // );
          // --- Simple warmup prediction ---
          try {
            // console.log("Warming up model...");
            // Fix: Replace null with a valid batch size (1)
            const shape = loadedModel.inputs[0].shape.slice();
            shape[0] = 1; // Replace null with batch size 1
            const warmupInput = tf.zeros(shape);

            const warmupResult = loadedModel.predict(warmupInput);
            await warmupResult.data(); // Wait for the computation
            tf.dispose([warmupInput, warmupResult]);
            // console.log("Model warmup complete.");
          } catch (warmupError) {
            console.error("Model warmup failed:", warmupError);
          }
          // --- End warmup ---
          // X

          modelRef.current = loadedModel;
          setIsModelReady(true);
          if (onModelStatusChange) onModelStatusChange(true);
          // console.log(
          //   "Wake word model loaded successfully (state set to true)."
          // );
        } else {
          // console.log("Component unmounted before model load finished.");
          if (loadedModel) tf.dispose(loadedModel); // Dispose if loaded but component gone
        }
      } catch (error) {
        console.error(
          "Error loading wake word model or setting backend:",
          error
        );
        if (localIsMounted) {
          setIsModelReady(false);
          if (onModelStatusChange) onModelStatusChange(false);
        }
      }
    };

    loadModel();

    // Cleanup function for the model loading effect
    return () => {
      // console.log(">>> AutoWakeWordLoader: Model loading effect cleanup.");
      localIsMounted = false;
      isMountedRef.current = false;
      // Dispose model on unmount
      if (modelRef.current) {
        modelRef.current.dispose();
        modelRef.current = null;
        // console.log("Wake word model disposed.");
      }
      // Full audio cleanup on unmount
      fullAudioCleanup();
    };
    // Make sure dependencies are correct. onModelStatusChange is used inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPath, onModelStatusChange]);

  // --- Full Audio Resource Cleanup ---
  const fullAudioCleanup = useCallback(() => {
    // console.log(">>> AutoWakeWordLoader: fullAudioCleanup called.");

    // Stop Meyda first
    if (meydaAnalyzerRef.current) {
      try {
        meydaAnalyzerRef.current.stop();
        // console.log("Meyda stopped (cleanup).");
      } catch (e) {
        console.warn("Meyda stop error during cleanup:", e);
      }
      meydaAnalyzerRef.current = null;
    }

    // Disconnect nodes
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.port.onmessage = null; // Remove listener
        workletNodeRef.current.disconnect();
        // console.log("Worklet node disconnected (cleanup).");
      } catch (e) {
        console.warn("Error disconnecting worklet:", e);
      }
      workletNodeRef.current = null;
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
        // console.log("Analyser node disconnected (cleanup).");
      } catch (e) {
        console.warn("Error disconnecting analyser:", e);
      }
      analyserRef.current = null;
    }
    if (microphoneSourceRef.current) {
      try {
        microphoneSourceRef.current.disconnect();
        // console.log("Mic source disconnected (cleanup).");
      } catch (e) {
        console.warn("Error disconnecting mic source:", e);
      }
      microphoneSourceRef.current = null;
    }

    // Close AudioContext
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      // console.log("Closing AudioContext...");
      audioContextRef.current
        .close()
        .then(() => console.log("AudioContext closed."))
        .catch((e) => console.error("AudioContext close error:", e));
    }
    audioContextRef.current = null;

    // Stop MediaStream Tracks
    if (streamRef.current) {
      // console.log("Stopping MediaStream tracks...");
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
          console.log(`Track ${track.id} stopped.`);
        }
      });
      streamRef.current = null;
      if (onStreamReady) {
        onStreamReady(null); // Notify parent stream is gone
      }
    }

    // Reset status refs/state
    isProcessingSetupRef.current = false;
    setIsProcessingSetup(false);
    detectionInProgressRef.current = false;
    mfccFramesRef.current = []; // Clear MFCC buffer

    // console.log("Full Audio Cleanup finished.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onStreamReady]); // onStreamReady is the key dependency here

  // --- Pause Audio Processing (Stops Meyda, disconnects analyser/worklet) ---
  const pauseAudioProcessing = useCallback(() => {
    if (!isProcessingSetupRef.current) {
      // console.log("Pause called but not processing setup (ref check).");
      return;
    }
    // console.log(">>> AutoWakeWordLoader: pauseAudioProcessing called.");

    // Stop Meyda
    if (meydaAnalyzerRef.current) {
      try {
        meydaAnalyzerRef.current.stop();
        console.log("Meyda stopped (pause).");
      } catch (e) {
        console.warn("Meyda stop error during pause:", e);
      }
      // Don't nullify meydaAnalyzerRef here, just stop it
    }

    // Disconnect nodes that feed data for processing
    // Order matters: disconnect downstream nodes first
    if (workletNodeRef.current) {
      try {
        // Disconnect worklet from any destinations it might have
        workletNodeRef.current.disconnect();
        workletNodeRef.current.port.onmessage = null; // Stop listening to messages
        // console.log(
        //   "Worklet node listener removed & disconnected from destinations (pause)."
        // );
      } catch (e) {
        console.warn("Error disconnecting worklet during pause:", e);
      }
    }
    if (analyserRef.current) {
      try {
        // Disconnect analyser from its destinations (e.g., worklet or destination)
        analyserRef.current.disconnect();
        // console.log("Analyser node disconnected from destinations (pause).");
      } catch (e) {
        console.warn("Error disconnecting analyser during pause:", e);
      }
    }
    if (microphoneSourceRef.current) {
      try {
        // Disconnect source from its destinations (e.g., analyser)
        microphoneSourceRef.current.disconnect();
        // console.log("Mic source node disconnected from destinations (pause).");
      } catch (e) {
        console.warn("Error disconnecting mic source during pause:", e);
      }
    }

    // Update status ref and state
    isProcessingSetupRef.current = false;
    setIsProcessingSetup(false);
    detectionInProgressRef.current = false; // Reset detection flag on pause

    // console.log("Wake word audio processing paused.");
  }, []); // No dependencies, operates on refs/state

  // --- Wake Word Detection Logic (Async, Triggered by Meyda Callback) ---
  const detectWakeWord = useCallback(async () => {
    // This function is now async and called via setTimeout from Meyda callback
    // It should NOT be called directly from a tight loop or message handler

    // Double check conditions, although caller should manage detectionInProgressRef
    if (
      !isProcessingSetupRef.current ||
      !enabled ||
      !modelRef.current ||
      detectionInProgressRef.current === false /* Should be true when called */
    ) {
      console.warn("detectWakeWord called unexpectedly or in invalid state.", {
        isProcessing: isProcessingSetupRef.current,
        enabled,
        modelReady: !!modelRef.current,
        detectionFlag: detectionInProgressRef.current,
      });
      // Reset flag just in case it was called wrongly without being set
      detectionInProgressRef.current = false;
      return;
    }

    const now = Date.now();
    if (now - lastDetectionTimeRef.current < DETECTION_COOLDOWN_MS) {
      // console.log("Detection cooldown active, skipping.");
      detectionInProgressRef.current = false; // Reset flag if skipping due to cooldown
      return;
    }

    // console.log(`>>> detectWakeWord: Processing buffered MFCCs.`);

    try {
      // --- Get MFCC Frames from Ref ---
      // We expect exactly TARGET_FRAMES_FOR_MODEL frames because the Meyda callback manages this
      const mfccFrames = mfccFramesRef.current;

      if (!mfccFrames || mfccFrames.length !== TARGET_FRAMES_FOR_MODEL) {
        console.warn(
          `>>> detectWakeWord: Incorrect number of MFCC frames buffered (${mfccFrames?.length}/${TARGET_FRAMES_FOR_MODEL}). Skipping.`
        );
        // Don't reset the buffer here necessarily, wait for Meyda to fill it correctly
        detectionInProgressRef.current = false; // Reset flag on unexpected buffer state
        return;
      }

      // --- Process the collected MFCC frames ---
      const inputTensor = tf.tidy(() => {
        // mfccFrames is already [TARGET_FRAMES_FOR_MODEL, N_MFCC]

        // Check for NaN/Infinity in input frames before processing
        let hasInvalidValue = false;
        for (const frame of mfccFrames) {
          if (!frame) {
            // Check if frame itself is null/undefined
            hasInvalidValue = true;
            console.error(
              ">>> detectWakeWord: Null/undefined frame found in MFCC buffer!"
            );
            break;
          }
          for (const val of frame) {
            if (!Number.isFinite(val)) {
              // Check individual MFCC value
              hasInvalidValue = true;
              console.error(
                `>>> detectWakeWord: Invalid value (${val}) found in MFCC frame!`
              );
              break;
            }
          }
          if (hasInvalidValue) break;
        }
        if (hasInvalidValue) {
          console.error(
            ">>> detectWakeWord: MFCC input frames contain invalid data! Clearing buffer."
          );
          mfccFramesRef.current = []; // Clear corrupt buffer
          return null; // Signal error up the chain
        }

        // Transpose MFCCs: from [frames, coeffs] to [coeffs, frames] for delta calculation
        const numFrames = TARGET_FRAMES_FOR_MODEL;
        const numCoeffs = N_MFCC;
        const baseMfccsCoeffFirst = Array(numCoeffs)
          .fill()
          .map(() => Array(numFrames).fill(0.0));
        for (let t = 0; t < numFrames; t++) {
          for (let c = 0; c < numCoeffs; c++) {
            // Add check for frame validity again just in case
            baseMfccsCoeffFirst[c][t] = mfccFrames[t]?.[c] ?? 0.0;
          }
        }

        // --- Calculate Deltas (Robust implementation needed) ---
        const calculateDeltas = (coeffs, N = 2) => {
          // N=context window (usually 2)
          const numCoeffs = coeffs.length;
          const numFrames = coeffs[0]?.length || 0;
          if (numFrames === 0) return [];

          const deltas = Array(numCoeffs)
            .fill()
            .map(() => Array(numFrames).fill(0.0));
          // Avoid division by zero if N=0, though N should be > 0
          const denom =
            N > 0
              ? 2 *
                Array.from({ length: N }, (_, i) => (i + 1) ** 2).reduce(
                  (a, b) => a + b,
                  0
                )
              : 1;

          for (let c = 0; c < numCoeffs; c++) {
            // Ensure coeffs[c] is valid before accessing its elements
            if (!coeffs[c]) {
              console.error(
                `>>> calculateDeltas: Missing coefficient array at index ${c}`
              );
              continue; // Skip this coefficient if data is missing
            }
            // Padding: Use first/last valid frame for padding
            const firstVal = coeffs[c][0] ?? 0.0;
            const lastVal = coeffs[c][numFrames - 1] ?? 0.0;
            const paddedCoeffs = [
              ...Array(N).fill(firstVal), // Pad beginning
              ...coeffs[c],
              ...Array(N).fill(lastVal), // Pad end
            ];
            for (let t = 0; t < numFrames; t++) {
              let numerator = 0;
              // Ensure indices for paddedCoeffs are valid
              for (let n_ = 1; n_ <= N; n_++) {
                const idx_plus = t + N + n_;
                const idx_minus = t + N - n_;
                const val_plus = paddedCoeffs[idx_plus] ?? 0.0;
                const val_minus = paddedCoeffs[idx_minus] ?? 0.0;
                numerator += n_ * (val_plus - val_minus);
              }
              deltas[c][t] = numerator / denom;
            }
          }
          return deltas;
        };

        const deltaMfccs = calculateDeltas(baseMfccsCoeffFirst);
        const deltaDeltaMfccs = calculateDeltas(deltaMfccs);

        // Combine features: [coeffs, frames] -> [120, frames]
        const combinedFeaturesList = [
          ...baseMfccsCoeffFirst,
          ...deltaMfccs,
          ...deltaDeltaMfccs,
        ];

        // Flatten features: [120, frames] -> Float32Array[120 * frames] (row-major for TF)
        const featuresData = new Float32Array(120 * numFrames);
        for (let featureIdx = 0; featureIdx < 120; featureIdx++) {
          // Check if the feature list entry exists
          const currentFeatureArray = combinedFeaturesList[featureIdx];
          if (!currentFeatureArray) {
            console.error(
              `>>> detectWakeWord: Missing feature array at combined index ${featureIdx}`
            );
            // Fill with zeros or handle as appropriate
            for (let frameIdx = 0; frameIdx < numFrames; frameIdx++) {
              featuresData[featureIdx * numFrames + frameIdx] = 0.0;
            }
            continue;
          }
          for (let frameIdx = 0; frameIdx < numFrames; frameIdx++) {
            // TF expects row-major: (feature * numFrames) + frame
            featuresData[featureIdx * numFrames + frameIdx] =
              currentFeatureArray[frameIdx] ?? 0.0;
          }
        }

        // Create tensor and reshape for model [1, 120, 63, 1]
        const featuresTensor = tf.tensor2d(
          featuresData,
          [120, numFrames],
          "float32"
        ); // Shape [120, 63]
        const expandedTensor = featuresTensor.expandDims(0).expandDims(-1); // Shape [1, 120, 63, 1]

        // Normalize
        return expandedTensor.sub(FEATURE_MEAN).div(FEATURE_STD);
      }); // End tf.tidy

      if (!inputTensor) {
        console.error(
          ">>> detectWakeWord: Failed to create input tensor (likely invalid MFCC data)."
        );
        // Buffer was likely cleared inside tidy block if data was corrupt
        detectionInProgressRef.current = false; // Reset flag on error
        return;
      }

      // --- Run Inference ---
      // console.log(">>> detectWakeWord: Calling model.predict...");
      const predictionStartTime = performance.now();
      const prediction = modelRef.current.predict(inputTensor);
      const score = await prediction.data(); // Get prediction data asynchronously
      const predictionEndTime = performance.now();
      // console.log(`>>> detectWakeWord: Prediction took ${ (predictionEndTime - predictionStartTime).toFixed(2) } ms.`);

      tf.dispose([inputTensor, prediction]); // Dispose tensors immediately
      // console.log(">>> detectWakeWord: Prediction complete.");

      const currentScore = score[0]; // Assuming model outputs a single score

      // --- Handle Score ---
      if (currentScore > detectionThreshold) {
        console.log(
          `%cWake word detected! Confidence: ${currentScore.toFixed(3)}`,
          "color: green; font-weight: bold;"
        );
        lastDetectionTimeRef.current = now; // Update last detection time *only on success*
        if (onWakeWordDetected) {
          try {
            onWakeWordDetected(currentScore);
          } catch (callbackError) {
            console.error(
              "Error in onWakeWordDetected callback:",
              callbackError
            );
          }
        }
        // --- Clear buffer and continue listening ---
        mfccFramesRef.current = []; // Clear buffer immediately to prevent re-trigger
        // console.log(">>> MFCC buffer cleared after detection.");
      } else {
        // Optional: Log low scores occasionally for debugging thresholds
        // if (Math.random() < 0.05) { // Log ~5% of non-detections
        //    console.log(`>>> detectWakeWord: Score below threshold: ${currentScore.toFixed(3)}`);
        // }
      }
    } catch (error) {
      console.error(">>> CRITICAL ERROR inside detectWakeWord:", error);
      // Attempt to recover gracefully
      mfccFramesRef.current = []; // Clear buffer on error
    } finally {
      // IMPORTANT: Always reset the flag, regardless of detection outcome or error
      detectionInProgressRef.current = false;
      // console.log(">>> detectWakeWord finished, detection flag reset.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled, // Check if enabled
    onWakeWordDetected, // Callback
    detectionThreshold, // Constant
    // Include other constants if they were props/state: N_MFCC, TARGET_FRAMES_FOR_MODEL etc.
    // Note: isProcessingSetupRef, modelRef are refs, don't need to be deps.
    // pauseAudioProcessing // Only if using Option A (stop on detect)
  ]);

  // --- Setup / Resume Audio Processing ---
  const setupOrResumeAudioProcessing = useCallback(async () => {
    // Prevent setup if already processing, model not ready, or component unmounted
    if (
      isProcessingSetupRef.current ||
      !isModelReady ||
      !isMountedRef.current
    ) {
      // console.log("Skipping setup/resume:", { isProcessing: isProcessingSetupRef.current, isModelReady, isMounted: isMountedRef.current });
      return;
    }
    // console.log("Setting up or resuming wake word audio processing...");
    detectionInProgressRef.current = false; // Ensure flag is reset at start

    try {
      // 1. Get MediaStream (only once per component life or if cleaned up)
      if (!streamRef.current) {
        // console.log("Requesting microphone access...");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: sampleRate,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }, // Added standard constraints
        });
        if (!isMountedRef.current) {
          // Check again after async getUserMedia
          stream.getTracks().forEach((track) => track.stop());
          // console.log("Component unmounted during microphone access grant.");
          return;
        }
        streamRef.current = stream;
        // console.log("Microphone access granted.");
        if (onStreamReady) {
          onStreamReady(streamRef.current);
        }
      }

      // 2. Get/Create/Resume AudioContext
      if (
        !audioContextRef.current ||
        audioContextRef.current.state === "closed"
      ) {
        // console.log("Creating or recreating AudioContext...");
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({ sampleRate });
        // console.log(
        //   `AudioContext created/recreated. State: ${audioContextRef.current.state}, Sample Rate: ${audioContextRef.current.sampleRate}`
        // );
        microphoneSourceRef.current = null; // Needs recreation if context is new
        analyserRef.current = null; // Needs recreation
        workletNodeRef.current = null; // Needs recreation
      }
      if (audioContextRef.current.state === "suspended") {
        // Attempt to resume context on user interaction if suspended
        try {
          await audioContextRef.current.resume();
          console.log("AudioContext resumed successfully.");
        } catch (resumeError) {
          console.error(
            "Failed to resume AudioContext programmatically. Requires user interaction.",
            resumeError
          );
          // You might want to display a message to the user asking them to click somewhere.
          // Set state to indicate manual resume needed?
        }
      }

      // 3. Create MediaStreamSource (if needed)
      if (
        !microphoneSourceRef.current &&
        streamRef.current &&
        audioContextRef.current
      ) {
        // console.log("Creating MediaStreamSource...");
        microphoneSourceRef.current =
          audioContextRef.current.createMediaStreamSource(streamRef.current);
        // console.log("MediaStreamSource created.");
      }

      // 4. Create AnalyserNode for Meyda (if needed)
      if (!analyserRef.current && audioContextRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = FFT_SIZE * 2; // Meyda usually expects 2x bufferSize for FFT
        analyserRef.current.smoothingTimeConstant = 0; // Typically 0 for analysis
        // console.log("Analyser node created.");
      }

      // 5. Create Worklet Node (Optional - purely for efficient data piping if needed elsewhere)
      // This example keeps it minimal. Remove if completely unused.
      if (!workletNodeRef.current && audioContextRef.current) {
        // console.log("Setting up AudioWorklet (passive listener)...");
        // Simplified Worklet - Just exists, does nothing actively here
        const workletCode = `
              class PassthroughProcessor extends AudioWorkletProcessor {
                process(inputs, outputs, parameters) {
                  return true; // Keep processor alive
                }
              }
              registerProcessor('passthrough-processor', PassthroughProcessor);
          `;
        const blob = new Blob([workletCode], {
          type: "application/javascript",
        });
        const workletUrl = URL.createObjectURL(blob);
        try {
          await audioContextRef.current.audioWorklet.addModule(workletUrl);
          workletNodeRef.current = new AudioWorkletNode(
            audioContextRef.current,
            "passthrough-processor"
          );
          // console.log("AudioWorklet node created (passthrough).");
          // !! DO NOT ATTACH onmessage listener here for wake word detection !!
        } catch (error) {
          console.error("Failed to add AudioWorklet module:", error);
          // If worklet fails, maybe continue without it? Or throw error?
          workletNodeRef.current = null; // Ensure it's null if setup failed
        } finally {
          URL.revokeObjectURL(workletUrl); // Clean up blob URL
        }
      }

      // 6. Connect Nodes: MicSource -> Analyser -> [Optional Worklet -> Destination]
      if (
        microphoneSourceRef.current &&
        analyserRef.current &&
        audioContextRef.current
      ) {
        // console.log("Connecting audio nodes: MicSource -> Analyser...");
        microphoneSourceRef.current.disconnect(); // Clear previous connections
        microphoneSourceRef.current.connect(analyserRef.current);

        // Optional: Connect Analyser to Worklet if it exists, otherwise connect Analyser elsewhere or leave it
        if (workletNodeRef.current) {
          // console.log("Connecting Analyser -> Worklet...");
          analyserRef.current.disconnect(); // Ensure analyser isn't connected elsewhere
          analyserRef.current.connect(workletNodeRef.current);
          // Optional: Connect worklet to destination if needed (e.g., for debugging audio)
          // workletNodeRef.current.connect(audioContextRef.current.destination);
          // console.log("Connecting Worklet -> Destination (optional)");
        } else {
          // If no worklet, analyser is connected only to MicSource
          // Optional: Connect analyser to destination? Usually not needed for analysis only.
          // analyserRef.current.connect(audioContextRef.current.destination);
          // console.log("Analyser connected. No worklet node in this path.");
        }
        // console.log("Audio node connection complete.");
      } else {
        console.error(
          "Skipping node connection - MicSource, Analyser, or AudioContext missing."
        );
        throw new Error("Audio node setup failed due to missing nodes."); // Abort setup
      }

      // 7. Setup/Start Meyda Analyzer (using the connected AnalyserNode)
      if (analyserRef.current && audioContextRef.current) {
        if (meydaAnalyzerRef.current) {
          // Stop existing instance if any
          try {
            meydaAnalyzerRef.current.stop();
          } catch (e) {
            /* ignore */
          }
          meydaAnalyzerRef.current = null;
        }

        // console.log("Creating Meyda Analyzer with callback for MFCCs...");
        meydaAnalyzerRef.current = Meyda.createMeydaAnalyzer({
          audioContext: audioContextRef.current,
          source: analyserRef.current, // Feed Meyda from the AnalyserNode
          bufferSize: FFT_SIZE, // Buffer size for analysis window
          hopSize: HOP_LENGTH, // Hop size for overlapping windows
          numberOfMFCCCoefficients: N_MFCC,
          sampleRate: audioContextRef.current.sampleRate,
          featureExtractors: ["mfcc"], // Only extract MFCCs
          callback: (features) => {
            // --- This callback runs on the MAIN THREAD ---
            // Keep it lightweight.

            // Check if processing is active, not paused, and no detection is already running
            if (
              !isProcessingSetupRef.current ||
              detectionInProgressRef.current ||
              !enabled
            ) {
              return;
            }

            if (features?.mfcc?.length === N_MFCC) {
              // Check for NaN/Infinity in Meyda's output *before* buffering
              if (features.mfcc.some((val) => !Number.isFinite(val))) {
                console.warn(
                  ">>> Meyda Callback received invalid MFCC values (NaN/Infinity). Skipping frame."
                );
                return; // Don't buffer invalid data
              }

              // --- Buffer the valid MFCC frame ---
              const currentFrames = mfccFramesRef.current;
              currentFrames.push(features.mfcc); // Add the new frame

              // Keep only the last N frames needed for the model (efficiently remove oldest)
              while (currentFrames.length > TARGET_FRAMES_FOR_MODEL) {
                currentFrames.shift();
              }
              // No need to reassign mfccFramesRef.current = currentFrames, push/shift modify in place.

              // --- Check if buffer is full and trigger detection ---
              if (currentFrames.length === TARGET_FRAMES_FOR_MODEL) {
                // console.log(`>>> Meyda Callback: MFCC buffer full (${currentFrames.length}). Scheduling detection.`);

                // Set flag immediately to prevent re-entry
                detectionInProgressRef.current = true;

                // *** Schedule the heavy lifting (detectWakeWord) to run ASAP ***
                // *** but *after* this callback finishes, freeing the main thread. ***
                setTimeout(() => {
                  // Check mount status again right before calling async function
                  if (!isMountedRef.current) {
                    // console.log(
                    //   "Component unmounted before scheduled detection could run."
                    // );
                    detectionInProgressRef.current = false; // Reset flag if unmounted
                    return;
                  }
                  // Check if still processing setup - might have been paused between schedule and run
                  if (!isProcessingSetupRef.current) {
                    // console.log(
                    //   "Processing was paused before scheduled detection could run."
                    // );
                    detectionInProgressRef.current = false; // Reset flag
                    return;
                  }
                  detectWakeWord(); // No need for await here, let it run async
                  // detectWakeWord handles resetting the flag internally via finally
                }, 0);
              }
            } else {
              // Only log occasionally if features are missing/wrong length
              // if (Math.random() < 0.01) {
              //    console.warn(">>> Meyda Callback received invalid/incomplete features:", features);
              // }
            }
          }, // End callback
        });

        try {
          meydaAnalyzerRef.current.start();
          // console.log("Meyda Analyzer started.");
        } catch (e) {
          console.error("Meyda start error:", e);
          throw e; // Propagate error to stop setup
        }
      } else {
        console.error("Cannot setup Meyda: Analyser or AudioContext missing.");
        throw new Error("Meyda setup failed."); // Abort setup
      }

      // --- Final step: Update state/ref to indicate processing is setup ---
      isProcessingSetupRef.current = true;
      setIsProcessingSetup(true);
      mfccFramesRef.current = []; // Ensure buffer is empty on start/resume
      console.log("Wake word audio processing setup/resume complete.");
    } catch (error) {
      console.error("Error setting up/resuming audio processing:", error);
      // Ensure ref/state are false on error
      isProcessingSetupRef.current = false;
      setIsProcessingSetup(false);
      detectionInProgressRef.current = false; // Reset flag
      // Attempt full cleanup on error to release resources
      fullAudioCleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isModelReady, // Wait for model
    enabled, // Prop check (though also checked in effect)
    onStreamReady, // Callback prop
    fullAudioCleanup, // Cleanup function
    detectWakeWord, // Detection logic (now called async)
    // Constants like N_MFCC, FFT_SIZE, HOP_LENGTH, TARGET_FRAMES_FOR_MODEL are stable
  ]);

  // --- Effect to Control Processing based on 'enabled' prop and Model Readiness ---
  useEffect(() => {
    // console.log(
    //   `>>> AutoWakeWordLoader: Audio Control Effect Run. enabled=${enabled}, isModelReady=${isModelReady}, isProcessingSetup=${isProcessingSetupRef.current}`
    // );
    if (enabled && isModelReady) {
      // Setup function checks isProcessingSetupRef internally before proceeding
      setupOrResumeAudioProcessing();
    } else {
      // If disabled OR model not ready, pause processing if it's currently running
      if (isProcessingSetupRef.current) {
        pauseAudioProcessing();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    isModelReady,
    setupOrResumeAudioProcessing, // Function ref
    pauseAudioProcessing, // Function ref
  ]);

  // This component doesn't render anything visible
  return null;
};

AutoWakeWordLoader.propTypes = {
  onModelStatusChange: PropTypes.func,
  onWakeWordDetected: PropTypes.func.isRequired,
  onStreamReady: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
};

// --- Default Props ---
AutoWakeWordLoader.defaultProps = {
  enabled: true,
  onModelStatusChange: () => {}, // Provide no-op default
};

// Memoize the component to prevent unnecessary re-renders if props don't change often.
// This is important because callback props (like onWakeWordDetected) might change identity
// if defined inline in the parent component on every render. Use useCallback in the parent
// for such props if memoization is critical.
export default React.memo(AutoWakeWordLoader);
